import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Box, FormControl, Grid, MenuItem, Paper, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { BoardDisclaimer } from '../../components/BoardComponents/BoardDisclaimer';
import { BoardLayout } from '../../components/BoardComponents/BoardLayout';
import { BoardPlate } from '../../components/BoardComponents/BoardPlate';
import { DocumentHyperlink } from '../../components/DocumentHyperlink';
import { useGlobal } from '../../components/GlobalProviderLayout';
import { MainContainer } from '../../components/MainContainerComp';
import { PlateModal } from '../../components/PlateModal';
import ScrollToTopButton from '../../components/ScrollTopButton';
import useScrollToElementOnLoad from '../../utils/hooks/useScrollToElementOnLoad';
import useVerticalScroll from '../../utils/hooks/useVerticalScroll';
import { IData, IPanel } from './UnionBoardData';
import { BOARDS, BOARD_SWITH_SLIPS } from './UnionBoardModels';
import {
  AdditionalInfoStyle,
  DisclaimerContainerStyle,
  DisclaimerContentStyle,
  DisclaimerStyle,
  PlaceholderStyle,
  ScoreBoardContainerStyle,
  ScoreBoardGridStyle,
  ScoreBoardIconStyle,
  SelectContainerStyle,
  SelectMenuStyle,
  SelectStyle,
  ViewSlipsStyle
} from './styled';

export const UnionBoard = ({
  data,
  handleBoardSelect,
  handleShiftDateSelect,
  handleClosePlateModal,
  handleOpenPlateModal,
  regIdForPlateModal
}: {
  data: IData,
  handleBoardSelect: (event: SelectChangeEvent<string>) => void
  handleShiftDateSelect: (event: SelectChangeEvent<string>, selectedBoard: string) => void,
  handleOpenPlateModal: (id: number) => void,
  regIdForPlateModal: number,
  handleClosePlateModal: () => void
}) => {
  const selectedBoard = data.selectedBoard ?? "";
  const selectedShift = data.selectedShift ?? "";
  const shifts = data.shifts ?? [];
  const callbackScore = data.scoreboard.callback ?? "";
  const panels = data.panels ?? [];
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const dropDownsFixedAtTop = useVerticalScroll();
  const { state: { button } } = useGlobal();
  const containerRef = useRef<HTMLDivElement>(null);
  useScrollToElementOnLoad({ elementId: button?.buttonImage, containerRef });

  const mapColumns = (panels: IPanel[]) => {
    return panels.map(p => {
      const maxButtonImages = Math.min(
        3,
        p.panel.reduce((max, bp) => Math.max(max, bp.buttons?.length || 0), 0)
      );

      return {
        rows: mapRows(p, maxButtonImages),
        maxButtonImages
      }
    })
  }

  const mapRows = (p: IPanel, maxButtonImages: number) => {
    return p.panel.map(bp => {
      const buttonImages = bp.buttons?.map(b => b.buttonImage) ?? [];
      const text = bp.calledBackText ?? "";
      return (
        <BoardPlate
          maxButtonImages={maxButtonImages}
          key={bp.plate}
          plateNumber={bp.plate}
          plateColor={bp.colour}
          plateName={bp.name}
          isCallback={bp.isCalledBack}
          isPlugin={false}
          blink={bp.isBlink || !!bp.buttons?.find(b => b.buttonImage === button?.buttonImage)}
          buttonImages={buttonImages}
          text={text}
          handlePlateNameClick={() => handleOpenPlateModal(bp.registrationNumber ?? 0)}
        />
      )
    })
  }

  return (
    <Box sx={{ marginBottom: 8 }}>
      <MainContainer hasFullSreenTabletSize>
        {showDisclaimer &&
          <Box sx={DisclaimerContainerStyle}>
            <Box sx={DisclaimerContentStyle}>
              <BoardDisclaimer handleClick={() => setShowDisclaimer(false)} />
            </Box>
          </Box>
        }
        <Box sx={SelectContainerStyle}>
          <Grid container sx={dropDownsFixedAtTop ? { position: 'fixed', top: '65px', left: 0, background: 'white', zIndex: 1, justifyContent: 'center' } : { justifyContent: 'center' }}>
            <Grid item xs={5.5} sm={4} lg={3}>
              <FormControl fullWidth>
                <Select
                  className='small'
                  sx={SelectStyle}
                  displayEmpty
                  value={selectedBoard}
                  onChange={handleBoardSelect}
                  data-automation="board-dropdown"
                  MenuProps={{ sx: SelectMenuStyle }}
                  renderValue={(board) => {
                    if (!board) return (
                      <Typography sx={PlaceholderStyle}>
                        Select a board
                      </Typography>
                    )
                    return BOARDS.filter(b => b.code === board).map(b => b.display)
                  }}
                >
                  {BOARDS.map(({ code, display }) => (
                    <MenuItem key={code} value={code}>
                      {display}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6.5} sm={5.5} lg={4}>
              <FormControl fullWidth>
                <Select
                  className='small'
                  sx={SelectStyle}
                  displayEmpty
                  value={selectedShift}
                  data-automation="shift-dropdown"
                  onChange={(e) => handleShiftDateSelect(e, selectedBoard)}
                  MenuProps={{ sx: SelectMenuStyle }}
                  renderValue={(shift) => {
                    if (!shift) return (
                      <Typography sx={PlaceholderStyle}>
                        Select a shift
                      </Typography>
                    )
                    const matchedShift = shifts.find(s => s.value === shift);
                    return matchedShift?.display;
                  }}
                >
                  {shifts.length > 0 &&
                    shifts.map(s => {
                      return (
                        <MenuItem key={s.value} value={s.value}>
                          {s.display}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} sm={9.5} lg={7}>
              <Grid container sx={AdditionalInfoStyle}>
                <Grid item xs={6} >
                  <Box
                    sx={DisclaimerStyle}
                    onClick={() => setShowDisclaimer(true)}
                    data-automation="disclaimer-link"
                  >
                    <ErrorOutlineRoundedIcon sx={{ marginRight: "3px" }} />
                    <Typography>Disclaimer</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={ViewSlipsStyle}>
                  {panels.length > 0 && BOARD_SWITH_SLIPS.includes(selectedBoard) &&
                    <DocumentHyperlink
                      link={`/union-board-slips/${selectedBoard}`}
                      icon={null}
                      target="_self"
                      dataAutomation='view-slips'
                      title='View Slips'
                    />
                  }
                </Grid>
                <Grid item xs={12} sx={ScoreBoardGridStyle}>
                  <Box sx={ScoreBoardContainerStyle}>
                    <Paper
                      elevation={0}
                      square
                      sx={ScoreBoardIconStyle}
                    />
                    <Typography data-automation="new-dispatch-called-back-indicator">
                      New Dispatch/Called back indicator: {callbackScore}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box>
          {panels.length > 0 ?
            <BoardLayout columns={mapColumns(panels)} containerRef={containerRef} /> :
            <Typography
              sx={{
                textAlign: 'center',
                py: 20,
              }}
              component={'div'}
              variant='container'
              data-automation="data-container"
            >
              <Typography data-automation="no-board">No board selected</Typography>
            </Typography>
          }
        </Box>
      </MainContainer >
      <ScrollToTopButton />
      <PlateModal
        id={regIdForPlateModal}
        onClose={handleClosePlateModal}
        open={!!regIdForPlateModal}
        board={data.selectedBoard}
      />
    </Box >
  )
}